import { qs, qsa } from '../lib/dom.js';

/**
 * Editorial text blocks that collapse AFTER the first scroll (audit "Раскрытие
 * текста").
 *
 * The rule the SEO spec asks for is the inverse of the obvious implementation:
 *
 *   • markup renders EXPANDED - no clamp in the server HTML and none in the
 *     default CSS, so a crawler (which renders CSS but never scrolls) and a
 *     visitor with JavaScript off both see the whole text;
 *   • the clamp is added by this module, and only once the visitor scrolls.
 *
 * Hence the CSS carries the clamp on `.is-collapsed`, never on the base class,
 * and the toggle button ships with `hidden` - without JavaScript it could not
 * work, so it must not be visible.
 *
 * A block is only collapsed if it is still BELOW the viewport when the scroll
 * happens. Clamping something the visitor is already looking at would yank the
 * page under them; the blocks live at the foot of the page, so in practice the
 * first scroll is always far above them.
 *
 * Blocks can also arrive LONG after load: the catalog replaces its whole bottom
 * block (price table + SEO copy) over AJAX on every filter change. Those have
 * missed the one-shot scroll pass, so they are armed by refreshCollapsibleText()
 * instead - see its note on why they are clamped on arrival.
 */

// Whether the one-shot "first scroll" pass has already run.
let scrolled = false;

export function initCollapsibleText() {
  // Arm whatever is already in the document. Note there is NO early return on
  // an empty page: a catalog can load with no collapsible block at all (a
  // filter combination with neither a price table nor SEO copy) and grow one
  // after the visitor filters, so the listeners below must exist regardless.
  refreshCollapsibleText(document);

  // The one-shot pass. Re-queries the document rather than closing over the
  // blocks found at load, so anything injected before the first scroll is
  // included.
  window.addEventListener('scroll', () => {
    scrolled = true;
    qsa('[data-collapsible]').forEach((block) => {
      // Already opened by hand? Leave it.
      if (block.dataset.collapseUserOpened === '1') return;
      // Only clamp what is still out of sight, so nothing jumps.
      if (block.getBoundingClientRect().top <= window.innerHeight) return;
      setCollapsed(block, true);
    });
  }, { passive: true, once: true });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-collapse-toggle]');
    if (!btn) return;
    const block = btn.closest('[data-collapsible]');
    if (!block) return;
    e.preventDefault();
    const nowCollapsed = !block.classList.contains('is-collapsed');
    setCollapsed(block, nowCollapsed);
    // Remember a manual open so the scroll pass cannot clamp it again.
    block.dataset.collapseUserOpened = nowCollapsed ? '0' : '1';
  });
}

/**
 * Arm collapsible blocks inside `root` - call it after replacing markup that
 * may contain some.
 *
 * Server markup always arrives expanded with the toggle `hidden` (that is what
 * a crawler must get), so freshly injected blocks need the toggle revealed, and
 * the clamp applied if the scroll pass they needed has already gone by. Without
 * this the catalog's SEO copy and price table stayed fully expanded with no
 * "show more" button from the first filter change until the next full reload.
 *
 * Unlike the scroll pass, this clamps regardless of where the block sits in the
 * viewport: the "don't yank it under the reader" rule protects text the visitor
 * might be reading, and this markup was created a moment ago. It collapses in
 * the same paint as the insert, so it simply appears collapsed.
 */
export function refreshCollapsibleText(root = document) {
  qsa('[data-collapsible]', root).forEach((block) => {
    const btn = qs('[data-collapse-toggle]', block);
    if (btn) btn.hidden = false; // the toggle only exists for JS users
    if (scrolled) setCollapsed(block, true);
  });
}

/** Apply or lift the clamp and keep the button in step with it. */
function setCollapsed(block, collapsed) {
  block.classList.toggle('is-collapsed', collapsed);
  const btn = qs('[data-collapse-toggle]', block);
  if (!btn) return;
  const label = qs('[data-collapse-label]', btn);
  if (label) {
    const more = btn.dataset.labelMore || label.textContent;
    const less = btn.dataset.labelLess || more;
    label.textContent = collapsed ? more : less;
  }
  btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
}
